<script setup>
import { ref, computed, reactive } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  inboxId: { type: [Number, String], required: true },
});

const emit = defineEmits(['created']);

const store = useStore();
const { t } = useI18n();

const dialogRef = ref(null);
const isSubmitting = ref(false);

const initialState = () => ({
  name: '',
  category: 'MARKETING',
  language: 'es',
  headerText: '',
  bodyText: '',
  bodyExample: '',
  footerText: '',
  buttons: [],
});

const form = reactive(initialState());

const open = () => {
  Object.assign(form, initialState());
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

defineExpose({ open, close });

const variableCount = computed(() => {
  const matches = form.bodyText.match(/{{\d+}}/g) || [];
  return new Set(matches).size;
});

const exampleValues = computed(() => {
  if (!form.bodyExample) return [];
  return form.bodyExample
    .split('|')
    .map(v => v.trim())
    .filter(Boolean);
});

const exampleMatchesVariables = computed(() => {
  if (variableCount.value === 0) return true;
  return exampleValues.value.length === variableCount.value;
});

const isValid = computed(() => {
  return (
    /^[a-z0-9_]{1,512}$/.test(form.name) &&
    form.bodyText.trim().length > 0 &&
    exampleMatchesVariables.value &&
    form.buttons.every(
      b =>
        b.text.trim() &&
        (b.type !== 'URL' || b.url) &&
        (b.type !== 'PHONE_NUMBER' || b.phone_number)
    )
  );
});

const addButton = type => {
  if (form.buttons.length >= 3) return;
  form.buttons.push({ type, text: '', url: '', phone_number: '' });
};

const removeButton = index => form.buttons.splice(index, 1);

const buildPayload = () => {
  const components = {};

  if (form.headerText.trim()) {
    components.header = { format: 'TEXT', text: form.headerText.trim() };
  }

  components.body = { text: form.bodyText.trim() };
  if (
    variableCount.value > 0 &&
    exampleValues.value.length === variableCount.value
  ) {
    components.body.example = exampleValues.value;
  }

  if (form.footerText.trim()) {
    components.footer = { text: form.footerText.trim() };
  }

  if (form.buttons.length) {
    components.buttons = form.buttons.map(b => {
      const button = { type: b.type, text: b.text.trim() };
      if (b.type === 'URL') button.url = b.url.trim();
      if (b.type === 'PHONE_NUMBER')
        button.phone_number = b.phone_number.trim();
      return button;
    });
  }

  return {
    name: form.name.trim().toLowerCase(),
    category: form.category,
    language: form.language,
    components,
  };
};

const handleSubmit = async () => {
  if (!isValid.value) return;
  isSubmitting.value = true;
  try {
    await store.dispatch('inboxes/createWhatsAppTemplate', {
      inboxId: props.inboxId,
      payload: buildPayload(),
    });
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.SUCCESS'));
    emit('created');
    close();
  } catch (error) {
    const msg =
      error?.response?.data?.error ||
      t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.ERROR');
    useAlert(msg);
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="2xl"
    :title="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.TITLE')"
    :description="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.DESCRIPTION')"
    :confirm-button-label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.SUBMIT')"
    :is-loading="isSubmitting"
    :disable-confirm-button="!isValid"
    overflow-y-auto
    position="top"
    @confirm="handleSubmit"
  >
    <div class="flex flex-col gap-4 max-h-[70vh] overflow-y-auto pr-2">
      <div class="grid grid-cols-2 gap-3">
        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME') }}
          <input
            v-model="form.name"
            type="text"
            :placeholder="
              t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME_PLACEHOLDER')
            "
            class="w-full px-3 py-2 text-sm rounded-lg bg-n-alpha-3 border border-n-weak text-n-slate-12 focus:outline-none focus:border-n-blue-7"
          />
          <span class="text-xs text-n-slate-10">
            {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME_HINT') }}
          </span>
        </label>

        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.LANGUAGE') }}
          <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
          <select
            v-model="form.language"
            class="w-full px-3 py-2 text-sm rounded-lg bg-n-alpha-3 border border-n-weak text-n-slate-12 focus:outline-none focus:border-n-blue-7"
          >
            <option value="es">Español (es)</option>
            <option value="es_MX">Español México (es_MX)</option>
            <option value="en">English (en)</option>
            <option value="en_US">English US (en_US)</option>
            <option value="pt_BR">Português Brasil (pt_BR)</option>
          </select>
          <!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
        </label>
      </div>

      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CATEGORY') }}
        <select
          v-model="form.category"
          class="w-full px-3 py-2 text-sm rounded-lg bg-n-alpha-3 border border-n-weak text-n-slate-12 focus:outline-none focus:border-n-blue-7"
        >
          <option value="MARKETING">
            {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CATEGORY_MARKETING') }}
          </option>
          <option value="UTILITY">
            {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CATEGORY_UTILITY') }}
          </option>
          <option value="AUTHENTICATION">
            {{
              t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CATEGORY_AUTHENTICATION')
            }}
          </option>
        </select>
      </label>

      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_OPTIONAL') }}
        <input
          v-model="form.headerText"
          type="text"
          maxlength="60"
          class="w-full px-3 py-2 text-sm rounded-lg bg-n-alpha-3 border border-n-weak text-n-slate-12 focus:outline-none focus:border-n-blue-7"
        />
      </label>

      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY') }}
        <textarea
          v-model="form.bodyText"
          rows="4"
          maxlength="1024"
          :placeholder="
            t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY_PLACEHOLDER')
          "
          class="w-full px-3 py-2 text-sm rounded-lg bg-n-alpha-3 border border-n-weak text-n-slate-12 focus:outline-none focus:border-n-blue-7"
        />
        <span class="text-xs text-n-slate-10">
          {{
            t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY_HINT', {
              count: variableCount,
            })
          }}
        </span>
      </label>

      <label
        v-if="variableCount > 0"
        class="flex flex-col gap-1 text-sm text-n-slate-12"
      >
        {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.EXAMPLE') }}
        <input
          v-model="form.bodyExample"
          type="text"
          :placeholder="
            t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.EXAMPLE_PLACEHOLDER')
          "
          class="w-full px-3 py-2 text-sm rounded-lg bg-n-alpha-3 border border-n-weak text-n-slate-12 focus:outline-none focus:border-n-blue-7"
        />
        <span
          class="text-xs"
          :class="
            exampleMatchesVariables ? 'text-n-slate-10' : 'text-n-ruby-10'
          "
        >
          {{
            t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.EXAMPLE_HINT', {
              count: variableCount,
            })
          }}
        </span>
      </label>

      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FOOTER_OPTIONAL') }}
        <input
          v-model="form.footerText"
          type="text"
          maxlength="60"
          class="w-full px-3 py-2 text-sm rounded-lg bg-n-alpha-3 border border-n-weak text-n-slate-12 focus:outline-none focus:border-n-blue-7"
        />
      </label>

      <div class="flex flex-col gap-2">
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS_TITLE') }}
          </span>
          <div class="flex gap-2">
            <button
              type="button"
              :disabled="form.buttons.length >= 3"
              class="px-2 py-1 text-xs rounded-md bg-n-alpha-3 text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-50"
              @click="addButton('QUICK_REPLY')"
            >
              + {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTON_QUICK_REPLY') }}
            </button>
            <button
              type="button"
              :disabled="form.buttons.length >= 3"
              class="px-2 py-1 text-xs rounded-md bg-n-alpha-3 text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-50"
              @click="addButton('URL')"
            >
              + {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTON_URL') }}
            </button>
            <button
              type="button"
              :disabled="form.buttons.length >= 3"
              class="px-2 py-1 text-xs rounded-md bg-n-alpha-3 text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-50"
              @click="addButton('PHONE_NUMBER')"
            >
              + {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTON_PHONE') }}
            </button>
          </div>
        </div>

        <div
          v-for="(btn, idx) in form.buttons"
          :key="idx"
          class="flex flex-col gap-2 p-3 rounded-lg bg-n-alpha-3 border border-n-weak"
        >
          <div class="flex items-center justify-between">
            <span class="text-xs font-medium text-n-slate-11">
              {{ btn.type }}
            </span>
            <button
              type="button"
              class="text-xs text-n-ruby-10 hover:underline"
              @click="removeButton(idx)"
            >
              {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.REMOVE') }}
            </button>
          </div>
          <input
            v-model="btn.text"
            type="text"
            maxlength="25"
            :placeholder="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTON_TEXT')"
            class="w-full px-3 py-2 text-sm rounded-lg bg-n-alpha-2 border border-n-weak text-n-slate-12 focus:outline-none focus:border-n-blue-7"
          />
          <input
            v-if="btn.type === 'URL'"
            v-model="btn.url"
            type="url"
            :placeholder="
              t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.URL_PLACEHOLDER')
            "
            class="w-full px-3 py-2 text-sm rounded-lg bg-n-alpha-2 border border-n-weak text-n-slate-12 focus:outline-none focus:border-n-blue-7"
          />
          <input
            v-if="btn.type === 'PHONE_NUMBER'"
            v-model="btn.phone_number"
            type="tel"
            :placeholder="
              t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.PHONE_PLACEHOLDER')
            "
            class="w-full px-3 py-2 text-sm rounded-lg bg-n-alpha-2 border border-n-weak text-n-slate-12 focus:outline-none focus:border-n-blue-7"
          />
        </div>
      </div>
    </div>
  </Dialog>
</template>
